import { useEffect, useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, fmtHUF } from "@/lib/api";
import { CheckCircle2, Clock } from "lucide-react";

const TYPES = [
  { key: "desk_hour", label: "Asztal / óra", unit: "hour", price: 1500 },
  { key: "desk_day", label: "Asztal / nap", unit: "day", price: 5000 },
  { key: "desk_month", label: "Asztal / hónap", unit: "month", price: 30000 },
  { key: "meeting_hour", label: "Tárgyaló / óra", unit: "hour", price: 6000 },
  { key: "office_day", label: "Egész iroda / nap", unit: "day", price: 30000 },
];

const isoDate = (d) => {
  if (!d) return null;
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
};

export default function BookingSection() {
  const [type, setType] = useState("desk_hour");
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [dayBooked, setDayBooked] = useState(false);
  const [startHour, setStartHour] = useState(null);
  const [duration, setDuration] = useState(1);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    notes: "",
    gdpr_accepted: false,
  });

  const cfg = useMemo(() => TYPES.find((t) => t.key === type), [type]);

  // Fetch availability when type or date changes
  useEffect(() => {
    const fetchAvail = async () => {
      if (!date) {
        setSlots([]);
        setDayBooked(false);
        return;
      }
      setLoadingAvail(true);
      try {
        const res = await api.get("/availability", {
          params: { booking_type: type, date: isoDate(date) },
        });
        if (res.data.unit === "hour") {
          setSlots(res.data.slots || []);
          setDayBooked(false);
        } else {
          setSlots([]);
          setDayBooked(!!res.data.booked);
        }
      } catch (e) {
        toast.error("Nem sikerült lekérni a szabad időpontokat");
      } finally {
        setLoadingAvail(false);
      }
    };
    fetchAvail();
    setStartHour(null);
  }, [type, date]);

  const totalPrice = useMemo(() => {
    if (!cfg) return 0;
    if (cfg.unit === "hour") return cfg.price * (duration || 1);
    return cfg.price;
  }, [cfg, duration]);

  const canSubmit = () => {
    if (!date) return false;
    if (cfg.unit === "hour" && startHour === null) return false;
    if (cfg.unit !== "hour" && dayBooked) return false;
    if (!form.customer_name || !form.customer_email || !form.customer_phone) return false;
    if (!form.gdpr_accepted) return false;
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) {
      toast.error("Kérlek tölts ki minden kötelező mezőt");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        booking_type: type,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        date: isoDate(date),
        notes: form.notes,
        gdpr_accepted: form.gdpr_accepted,
      };
      if (cfg.unit === "hour") {
        payload.start_hour = startHour;
        payload.duration_hours = duration;
      }
      const res = await api.post("/bookings", payload);
      setSuccess(res.data);
      toast.success("Foglalás rögzítve! Visszaigazoló e-mailt küldtünk.");
      setForm({ customer_name: "", customer_email: "", customer_phone: "", notes: "", gdpr_accepted: false });
      setStartHour(null);
      setDate(undefined);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Hiba a foglalás során";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <section
      id="foglalas"
      data-testid="booking-section"
      className="py-24 md:py-32 bg-[#FAF9F6]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 mb-16 gap-8 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-6">Foglalás</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A]">
              Válassz típust, dátumot,<br /><span className="italic">és érkezz meg.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 text-[#595959] text-[15px] leading-relaxed">
            A naptáron a szabad napok érhetők el, az időpont-kártyák a foglalt órákat
            áthúzva jelenítik meg. A visszaigazolást e-mailben küldjük.
          </div>
        </div>

        {success ? (
          <div
            data-testid="booking-success"
            className="bg-white border border-[#E5E0D8] p-10 md:p-16 max-w-3xl mx-auto text-center"
          >
            <CheckCircle2 className="w-12 h-12 text-[#3E5C46] mx-auto mb-6" />
            <h3 className="font-serif-display text-3xl md:text-4xl mb-4">Köszönjük a foglalást!</h3>
            <p className="text-[#595959] mb-8 leading-relaxed">
              Visszaigazoló e-mailt küldtünk a <b>{success.customer_email}</b> címre.
              <br />Foglalás típusa: <b>{success.type_label}</b> · {success.date}
              {success.start_hour !== null && success.start_hour !== undefined && (
                <> · {String(success.start_hour).padStart(2, "0")}:00 – {String(success.start_hour + (success.duration_hours || 1)).padStart(2, "0")}:00</>
              )}
              <br />Fizetendő összeg: <b>{fmtHUF(success.total_price)}</b>
            </p>
            <button
              onClick={() => setSuccess(null)}
              data-testid="booking-success-reset"
              className="btn-outline"
            >
              Új foglalás
            </button>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="booking-form"
            className="grid lg:grid-cols-12 gap-px bg-[#E5E0D8] border border-[#E5E0D8]"
          >
            {/* Step 1 – Type */}
            <div className="lg:col-span-12 bg-white p-8 md:p-10">
              <Label className="eyebrow block mb-4">01 · Típus</Label>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {TYPES.map((t) => (
                  <button
                    type="button"
                    key={t.key}
                    data-testid={`book-type-${t.key}`}
                    onClick={() => setType(t.key)}
                    className={`text-left p-4 border transition-all duration-300 ${
                      type === t.key
                        ? "border-[#1A1A1A] bg-[#1A1A1A] text-white"
                        : "border-[#E5E0D8] bg-white text-[#1A1A1A] hover:border-[#1A1A1A]"
                    }`}
                  >
                    <div className="font-serif-display text-xl leading-tight">{t.label}</div>
                    <div className={`text-[11px] tracking-[0.2em] uppercase mt-2 ${type === t.key ? "text-white/70" : "text-[#BFA588]"}`}>
                      {fmtHUF(t.price)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2 – Date */}
            <div className="lg:col-span-6 bg-white p-8 md:p-10">
              <Label className="eyebrow block mb-4">02 · Dátum</Label>
              <div data-testid="booking-calendar">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < today}
                  fromDate={today}
                  className="rounded-none border border-[#E5E0D8] p-3"
                />
              </div>
              {date && (
                <p className="mt-4 text-sm text-[#595959]" data-testid="selected-date">
                  Választott dátum: <b className="text-[#1A1A1A]">{isoDate(date)}</b>
                </p>
              )}
            </div>

            {/* Step 3 – Time slot or info */}
            <div className="lg:col-span-6 bg-white p-8 md:p-10">
              <Label className="eyebrow block mb-4">03 · {cfg.unit === "hour" ? "Időpont" : "Foglaltság"}</Label>

              {!date && (
                <p className="text-[#595959] text-sm">Válassz dátumot a baloldalon.</p>
              )}

              {date && cfg.unit === "hour" && (
                <>
                  {loadingAvail ? (
                    <p className="text-sm text-[#595959]">Időpontok betöltése…</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="hour-slots">
                      {slots.map((s) => (
                        <button
                          type="button"
                          key={s.hour}
                          data-testid={`slot-${s.hour}`}
                          disabled={s.booked}
                          onClick={() => setStartHour(s.hour)}
                          className={`relative py-3 text-[13px] border transition-all duration-200 ${
                            s.booked
                              ? "border-[#E5E0D8] text-[#BFA588] line-through cursor-not-allowed bg-[#FAF9F6]"
                              : startHour === s.hour
                              ? "border-[#1A1A1A] bg-[#1A1A1A] text-white"
                              : "border-[#E5E0D8] text-[#1A1A1A] hover:border-[#1A1A1A]"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {startHour !== null && (
                    <div className="mt-6">
                      <Label className="eyebrow block mb-2">Időtartam</Label>
                      <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                        <SelectTrigger data-testid="duration-select" className="w-full rounded-none border-[#E5E0D8]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8].map((h) => (
                            <SelectItem key={h} value={String(h)}>{h} óra</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}

              {date && cfg.unit !== "hour" && (
                <div
                  data-testid="day-availability"
                  className={`p-6 border ${dayBooked ? "border-[#9E2A2B] text-[#9E2A2B]" : "border-[#3E5C46] text-[#3E5C46]"}`}
                >
                  {loadingAvail
                    ? "Foglaltság ellenőrzése…"
                    : dayBooked
                    ? "Ez a nap sajnos már foglalt. Válassz másik napot."
                    : "A választott nap szabad. Folytasd az adatok kitöltésével."}
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-[#E5E0D8] flex items-center justify-between" data-testid="total-summary">
                <div className="flex items-center gap-2 text-[#595959] text-sm">
                  <Clock className="w-4 h-4" /> Becsült összeg
                </div>
                <div className="font-serif-display text-3xl text-[#1A1A1A]" data-testid="total-amount">
                  {fmtHUF(totalPrice)}
                </div>
              </div>
            </div>

            {/* Step 4 – Customer details */}
            <div className="lg:col-span-12 bg-white p-8 md:p-10">
              <Label className="eyebrow block mb-6">04 · Adatok</Label>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="b-name" className="text-[12px] tracking-[0.18em] uppercase text-[#595959]">Név *</Label>
                  <Input
                    id="b-name"
                    data-testid="booking-name"
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    required
                    className="mt-2 rounded-none border-0 border-b border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                  />
                </div>
                <div>
                  <Label htmlFor="b-email" className="text-[12px] tracking-[0.18em] uppercase text-[#595959]">E-mail *</Label>
                  <Input
                    id="b-email"
                    type="email"
                    data-testid="booking-email"
                    value={form.customer_email}
                    onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                    required
                    className="mt-2 rounded-none border-0 border-b border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                  />
                </div>
                <div>
                  <Label htmlFor="b-phone" className="text-[12px] tracking-[0.18em] uppercase text-[#595959]">Telefonszám *</Label>
                  <Input
                    id="b-phone"
                    data-testid="booking-phone"
                    value={form.customer_phone}
                    onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                    required
                    className="mt-2 rounded-none border-0 border-b border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="b-notes" className="text-[12px] tracking-[0.18em] uppercase text-[#595959]">Megjegyzés</Label>
                  <Textarea
                    id="b-notes"
                    data-testid="booking-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    className="mt-2 rounded-none border-0 border-b border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0"
                  />
                </div>
              </div>

              <div className="mt-8 flex items-start gap-3">
                <Checkbox
                  id="b-gdpr"
                  data-testid="booking-gdpr"
                  checked={form.gdpr_accepted}
                  onCheckedChange={(v) => setForm({ ...form, gdpr_accepted: !!v })}
                  className="mt-1 rounded-none"
                />
                <label htmlFor="b-gdpr" className="text-sm text-[#595959] leading-relaxed">
                  Elolvastam és elfogadom az adatkezelési tájékoztatót. Hozzájárulok ahhoz, hogy a megadott adatokat a foglalás teljesítése érdekében kezeljék. *
                </label>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <div className="text-[#595959] text-sm">
                  Fizetés a helyszínen vagy átutalással · Lemondás 24 órán belül díjmentes
                </div>
                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={submitting || !canSubmit()}
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? "Küldés…" : "Foglalás megerősítése"}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
